<template>
  <FreeBottomSheet
    :name="name"
    @onShow="emit('onShow')"
    @onClose="emit('onClose')"
  >
    <template #header="{ hide }">
      <div class="bg-background-neutral-primary mb-4 rounded-t-3xl px-4">
        <slot name="header" v-bind="{ hide }" />
        <CalendarWeekdays />
      </div>
    </template>
    <template #default="{ hide }">
      <div class="bg-background-neutral-primary space-y-6 px-4">
        <CalendarContainer
          :mode="mode"
          :prices="prices"
          :min-date="dateLimits.min"
          :max-date="dateLimits.max"
          :selected-dates="selectedDates"
          :marked-dates="markedDates"
          :available-dates="availableDates"
          :number-of-months="numberOfMonths"
          :is-new-calendar-version="isNewCalendarVersion"
          :is-filtered="isFiltered"
          :is-loading="isLoading"
          @onDateChange="handleDateChange"
          @onDisabledDateSelect="emit('onDisabledDateSelect')"
          @onCalendarIntersect="emit('onCalendarIntersect', $event)"
        />
        <div class="sticky bottom-[26px] flex w-full">
          <slot name="footer" v-bind="{ hide }" />
        </div>
      </div>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { searchFormCalendar } from 'shared';
  import type { ConfigType } from 'dayjs';

  import { FreeBottomSheet } from '../../../shared/ui';
  import CalendarWeekdays from './CalendarWeekdays.vue';
  import CalendarContainer from './calendar-content/CalendarContainer.vue';
  import type { CalendarPrices } from '../types';

  interface Props {
    name: string;
    selectedDates?: string[];
    markedDates?: string[];
    availableDates?: Array<string>;
    mode?: searchFormCalendar.config.CalendarMode;
    prices?: CalendarPrices | null;
    minDate?: ConfigType;
    maxDate?: ConfigType;
    numberOfMonths: number;
    isNewCalendarVersion?: boolean;
    isLoading?: boolean;
    isFiltered?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    selectedDates: () => [],
    availableDates: undefined,
    markedDates: () => [],
    mode: () => searchFormCalendar.config.CALENDAR_MODES.SINGLE,
    prices: null,
    minDate: '',
    maxDate: '',
    isNewCalendarVersion: false,
    isLoading: false,
  });

  const emit = defineEmits<{
    onShow: [];
    onClose: [];
    onDatesSelect: [dates: Array<string>];
    onDisabledDateSelect: [];
    onCalendarIntersect: [visibleItems: number];
  }>();

  const isSingleMode = computed(
    () => props.mode === searchFormCalendar.config.CALENDAR_MODES.SINGLE,
  );

  const dateLimits = computed(() =>
    searchFormCalendar.lib.getCalendarDateLimits(props.maxDate, props.minDate),
  );
  const handleDateChange = (date: string) => {
    const [firstInitial, secondInitial] = props.selectedDates;
    const firstDate = isSingleMode.value || !firstInitial ? date : firstInitial;
    const secondDate = !isSingleMode.value ? date : secondInitial;

    const newDates = searchFormCalendar.lib.buildNewDates({
      firstDate,
      secondDate,
      isSingleMode: isSingleMode.value,
    });
    emit('onDatesSelect', newDates);
  };
</script>
